import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { MessageInfo } from '@lub0v/santa-utils';
import { Menu } from '@src/components/Menu/Menu';
import { PencilIcon } from '@src/components/icons/PencilIcon';
import { canEditMessage } from './messageEditWindow';
import { TrashIcon } from '@src/components/icons/TrashIcon';

export function MessageBubble({
  message,
  disabled,
  onEdit,
  onDelete,
  children,
}: {
  message: MessageInfo;
  disabled: boolean;
  onEdit: () => void;
  onDelete: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const bubbleRef = useRef<HTMLElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const suppressClick = useRef(false);
  const allowed = message.isYou && message.active !== false && !disabled;
  function cancel() {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    start.current = null;
  }
  useEffect(() => cancel, []);
  function open() {
    cancel();
    setMenuOpen(true);
  }
  return (
    <article
      ref={bubbleRef}
      tabIndex={allowed ? 0 : undefined}
      aria-haspopup={allowed ? 'menu' : undefined}
      aria-expanded={allowed ? menuOpen : undefined}
      onDoubleClick={(event) => {
        if (allowed && !(event.target as Element).closest('a,button')) {
          event.preventDefault();
          open();
        }
      }}
      onKeyDown={(event) => {
        if (
          allowed &&
          event.target === event.currentTarget &&
          (event.key === 'Enter' ||
            event.key === ' ' ||
            event.key === 'ContextMenu' ||
            (event.shiftKey && event.key === 'F10'))
        ) {
          event.preventDefault();
          open();
        }
      }}
      className={`messenger__message${message.isYou ? ' messenger__message--outgoing' : ''}`}
      data-testid={`game-message-${message.id}`}
      onPointerDown={(event) => {
        if (!allowed || event.button !== 0 || (event.target as Element).closest('a,button')) return;
        cancel();
        suppressClick.current = false;
        start.current = { x: event.clientX, y: event.clientY };
        timer.current = setTimeout(() => {
          open();
          suppressClick.current = true;
        }, 500);
      }}
      onPointerMove={(event) => {
        if (
          start.current &&
          (Math.abs(event.clientX - start.current.x) > 10 ||
            Math.abs(event.clientY - start.current.y) > 10)
        )
          cancel();
      }}
      onPointerUp={cancel}
      onPointerCancel={cancel}
      onPointerLeave={cancel}
      onClickCapture={(event) => {
        if (suppressClick.current && !(event.target as Element).closest('button,[role="menu"]')) {
          event.preventDefault();
          event.stopPropagation();
          suppressClick.current = false;
        }
      }}
      onContextMenu={(event) => {
        if (allowed) {
          event.preventDefault();
          open();
        }
      }}
    >
      {children}
      {message.isYou && message.active !== false ? (
        <Menu
          anchorRef={bubbleRef}
          open={menuOpen}
          onOpenChange={setMenuOpen}
          label={t('game.messenger.actions')}
          testId={`game-message-${message.id}-actions`}
          items={[
            ...(!message.isAutomatic && canEditMessage(message.timestamp)
              ? [
                  {
                    id: 'edit',
                    label: t('game.messenger.edit'),
                    icon: <PencilIcon />,
                    disabled,
                    onSelect: onEdit,
                  },
                ]
              : []),
            {
              id: 'delete',
              label: t('game.messenger.delete'),
              icon: <TrashIcon />,
              danger: true,
              disabled,
              onSelect: onDelete,
            },
          ]}
        />
      ) : null}
    </article>
  );
}
